import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightIcon,
  BeakerIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { getChemicalInventorySettings } from "../../../api/hse/chemicalInventorySettings";
import { normalizeChemicalInventorySettings } from "../../../utils/chemicalInventoryAccess";
import { chemicalInventorySetupIssues } from "./setupGuideStatus";

const GuideTaskRow = ({ icon: Icon, title, status, description, to, linkLabel }) => {
  const tone =
    status === "ok"
      ? "border-emerald-200 bg-emerald-50/50"
      : status === "warn"
        ? "border-amber-200 bg-amber-50/40"
        : "border-slate-200 bg-slate-50/60";

  const StatusIcon =
    status === "ok" ? CheckCircleIcon : status === "warn" ? ExclamationTriangleIcon : InformationCircleIcon;

  const statusColor =
    status === "ok" ? "text-emerald-700" : status === "warn" ? "text-amber-800" : "text-slate-600";

  return (
    <div className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${tone}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className={`mt-0.5 flex items-center gap-1.5 text-xs font-medium ${statusColor}`}>
            <StatusIcon className="h-4 w-4 shrink-0" />
            <span>{description}</span>
          </div>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
        >
          {linkLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
      ) : null}
    </div>
  );
};

export const CompanyGuideChemicalInventorySection = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(false);
      try {
        const raw = await getChemicalInventorySettings();
        if (cancelled) return;
        setSettings(normalizeChemicalInventorySettings(raw));
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setSettings(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const issueSet = useMemo(
    () => new Set(chemicalInventorySetupIssues(settings, { loadError })),
    [settings, loadError]
  );

  const loaded = settings !== null && !loadError;

  const tasks = useMemo(() => {
    if (!loaded) return [];
    const row = (id) => ({
      id,
      icon: BeakerIcon,
      title: t(`company_guide.chemical_inventory.task.${id}.title`),
      status: issueSet.has(id) ? "warn" : "ok",
      description: t(
        issueSet.has(id)
          ? `company_guide.chemical_inventory.task.${id}.warn`
          : `company_guide.chemical_inventory.task.${id}.ok`
      ),
      to: "/dashboard/hse-ks/settings?tab=chemical_inventory#chemical-inventory-setup-status",
      linkLabel: t("company_guide.chemical_inventory.link.settings"),
    });
    return [row("responsible"), row("visibility"), row("interval")];
  }, [issueSet, loaded, t]);

  return (
    <section id="chemical-inventory-setup-status" className="scroll-mt-24">
      <div className="mb-4 flex items-center gap-2">
        <BookOpenIcon className="h-5 w-5 text-slate-600" />
        <h3 className="text-base font-semibold text-slate-900">
          {t("company_guide.chemical_inventory.heading")}
        </h3>
      </div>
      <p className="mb-4 text-sm text-slate-600">
        {t("company_guide.chemical_inventory.subtitle")}
      </p>
      {loadError ? (
        <p className="text-sm text-amber-800">
          {t("company_guide.chemical_inventory.load_error")}
        </p>
      ) : !loaded ? (
        <p className="text-sm text-slate-500">
          {t("company_guide.chemical_inventory.loading")}
        </p>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <GuideTaskRow key={task.id} {...task} />
          ))}
        </div>
      )}
    </section>
  );
};
